import { ClassKey } from "@const/class.constants";
import { XbotPageContext, XbotStartersAbout, XbotStartersNote } from "@dto/pyxbot-agent.dto";
import { shownGraphWindow } from "src/component/graph/shownGraphWindow";
import { MenuNode } from "src/model/MenuNode";
import { GLOB } from "src/util/Glob";
import { kindPlatformText } from "./agent/format";
import { textOf } from "./xbotFetch";

// Nothing here is worked out: values are read off the current page as-is; no item means nothing is handed over.

type PageWindow = XbotPageContext["window"];

/** The classes that are monitored estate. A settings or dashboard page has no item to ask about. */
const ESTATE_CLASSES = new Set<string>(Object.values(ClassKey));

/** What a preset window is called in a sentence. The graph controls offer these four and custom. */
const WINDOW_LABELS: Record<string, string> = {
  day: "last day",
  week: "last week",
  month: "last month",
  year: "last year",
};

const CUSTOM_WINDOW_LABEL = "custom range";

/** The part of a menu tree node the page is read from; the app's MenuNode fits it, tests build plain ones. */
export interface PageNode {
  itemId?: string;
  titleText?: string;
  title?: unknown;
  subsystem?: string;
  hwType?: string | string[];
  isLeaf?: boolean;
  parent?: PageNode;
}

/** The part of an item id that names the object; the tree may add the group it shows the object in. */
function objectIdOf(itemId: unknown): string {
  const id = textOf(itemId);
  const group = id.indexOf("&group_id=");
  return group < 0 ? id : id.slice(0, group);
}

/** Climbs to the highest ancestor sharing the same item id, preferring the one that carries a kind (subsystem). */
export function objectNodeOf(node: PageNode | undefined): PageNode | undefined {
  const item = objectIdOf(node?.itemId);
  if (!node || !item) {
    return undefined;
  }
  let highest = node;
  let withKind: PageNode | undefined = textOf(node.subsystem) ? node : undefined;
  for (let up = node.parent; up && objectIdOf(up.itemId) === item; up = up.parent) {
    highest = up;
    if (textOf(up.subsystem)) {
      withKind = up;
    }
  }
  return withKind ?? highest;
}

export function currentPageContext(): XbotPageContext | undefined {
  const node = GLOB.selectedItem as PageNode | undefined;
  const itemId = textOf(node?.itemId);
  const object = objectNodeOf(node);
  const label = nodeLabel(object);
  const cls = textOf(GLOB.selectedClass).replace(/^\//, "");
  if (!itemId || !label || !ESTATE_CLASSES.has(cls)) {
    return undefined;
  }
  const platform = hwTypeOf(node);
  const kind = textOf(object?.subsystem);
  const page = textOf(GLOB.location?.pathname);
  // the window belongs to the page's graph controls, not the chat
  const window = shownGraphWindow();
  return {
    item_id: itemId, label, class: cls, ...(platform ? { platform } : {}), ...(kind ? { kind } : {}), ...(page ? { page } : {}),
    ...(window ? { window } : {}),
  };
}

/** Prefers NG's confirmed `about` naming over the page's own labels; no bar at all when NG hides the object. */
export function askingAboutText(
  context: XbotPageContext | null | undefined,
  about?: XbotStartersAbout | null,
  note?: XbotStartersNote | null,
): string {
  if (!context || note === "page_not_visible") {
    return "";
  }
  const name = textOf(about?.name) || textOf(context.label) || textOf(context.item_id);
  if (!name) {
    return "";
  }
  const what = about ? kindPlatformText(about) : kindPlatformText({ kind: context.kind, platform: context.platform });
  const head = what ? `${name} (${what})` : name;
  const when = windowLabel(context.window);
  return "Asking about: " + (when ? `${head} · ${when}` : head);
}

export function windowLabel(window: PageWindow): string {
  if (!window) {
    return "";
  }
  const preset = textOf(window.preset);
  if (preset) {
    return WINDOW_LABELS[preset] || preset;
  }
  return window.start && window.end ? CUSTOM_WINDOW_LABEL : "";
}

function nodeLabel(node: PageNode | undefined): string {
  return textOf(node?.titleText) || textOf(node?.title);
}

/** The item's platform is on the nearest node above it that has one - that is where the tree keeps it. */
function hwTypeOf(node: PageNode | undefined): string {
  const hwType = MenuNode.getHwTypeParent(node as MenuNode)?.hwType;
  return textOf(Array.isArray(hwType) ? hwType[0] : hwType);
}
